<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Pending Payouts</h2>
    </header>

    <ReferralsTable
      :tColumns="$options.tColumns"
      :tData="referrers"
      :tNestedColumns="$options.tNestedColumns"
      :tNestedData="referralsPending"
      :styles="{ ...$options.styles }"
      :referrals="referralsPending"
      :getReferrals="getReferralsByReferrer"
      :getTData="getReferrers"
      :withActionsCol="true"
    >
      <template #table-data-actions="trData">
        <button class="mt-1 shared-table-cells-button" @click="handlePaymentModal(trData)">
          Pay
        </button>
      </template>
    </ReferralsTable>

    <!-- Payment modal -->
    <ModalPlus
      :show="showPaymentModal"
      :onClose="handleClosePaymentModal"
      :onProceed="handlePayReferral"
      title="Referral Payout"
      buttonText="Pay"
      maxWidth="900px"
    >
      <div class="px-8 pb-8">
        <form class="form" @submit.prevent="">
          <div class="px-0 pb-0 form-fields">
            <div>
              <div class="mb-6 ml-6 text-xs font-semibold">Choose a payout amount*</div>
              <div class="grid grid-cols-3 col-gap-8">
                <div
                  v-for="(pType, index) in payoutTypes"
                  :key="`payout-type-${index}`"
                  class="flex items-center mb-6"
                >
                  <div class="ratio">
                    <input
                      type="radio"
                      class="ratio-input"
                      :id="pType.name"
                      :value="pType.name"
                      :disabled="pType.name === 'previous' && pType.value <= referrer.balance > 0"
                      v-model="ratioValue"
                    />
                    <label class="ratio-label" :for="pType.name"></label>
                  </div>

                  <div class="mb-2">
                    <VFormInput
                      placeholder="Custom amount"
                      labelPosition="top"
                      :label="pType.label"
                      :onlyDigits="true"
                      :initialValue="pType.value"
                      :classes="{ fieldContainer: 'mb-0', field: 'max-w-xxs h-12' }"
                      :leftIcon="true"
                      :reset="pType.name === 'custom' && !showModal"
                      :disabled="
                        pType.name === 'all' ||
                        pType.name === 'previous' ||
                        payoutType.name !== 'custom'
                      "
                      @update="value => handleInputUpdate(pType.name, value)"
                    >
                      <template #left>$</template>
                    </VFormInput>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div
          class="px-4 py-4 text-sm leading-loose border rounded border-ricoma-gray-200 bg-ricoma-gray-100"
        >
          <div class="py-1">
            <span class="inline-block font-semibold min-w-32">Referrer's Name:</span>
            <span class="px-2">{{ referrer.first_name }} {{ referrer.last_name }}</span>
          </div>
          <div class="py-1">
            <span class="inline-block font-semibold min-w-32">Payout Amount:</span>
            <span class="px-2 font-bold">
              {{
                (payoutType.value && addDollarSymbol(parseFloat(payoutType.value).toFixed(2))) ||
                '$0'
              }}
            </span>
          </div>
          <div class="py-1">
            <span class="inline-block font-semibold min-w-32">Current Balance:</span>
            <span class="px-2">
              {{
                (referrer.balance && addDollarSymbol(parseFloat(referrer.balance).toFixed(2))) ||
                '$0'
              }}
            </span>
          </div>
          <div class="py-1">
            <span class="inline-block font-semibold min-w-32">Estimated New Balance:</span>
            <span class="px-2">
              {{
                (referrer.balance &&
                  addDollarSymbol(parseFloat(referrer.balance - payoutType.value).toFixed(2))) ||
                '$0'
              }}
            </span>
          </div>
        </div>
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { safeNullDataset, addDollarSymbol, normalizeAdminReferrers } from '@/utils';

import VFormInput from '@/components/base/VFormInput';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import ReferralsTable from '@/components/admin/ReferralsTable';

const PAYOUT_TYPES = [
  { name: 'all', label: 'Current Balance', value: 0 },
  { name: 'previous', label: 'Past Statement Balance', value: 0 },
  { name: 'custom', label: 'Custom Amount', value: 0 },
];

const normalizeCb = data => ({
  'total balance':
    (data.balance && addDollarSymbol(parseFloat(data.balance).toFixed(2))) || '$0.00',
});

export default {
  name: 'AdminReferralsPendingPayouts',
  components: {
    VFormInput,
    ModalPlus,
    ReferralsTable,
  },
  data() {
    return {
      showModal: false,
      showPaymentModal: false,
      orderDetails: [],
      referrer: {},
      csvData: [],
      payoutTypes: PAYOUT_TYPES,
      payoutType: PAYOUT_TYPES[2],
      ratioValue: 'custom',
    };
  },
  watch: {
    ratioValue(val) {
      this.payoutType = this.payoutTypes.find(({ name }) => name === val);
    },
    referrer(referrer) {
      // update balance and previous balance input to reflect referrer's values
      if (Object.keys(referrer).length) {
        const { balance, previous_balance } = referrer;
        this.payoutTypes = this.payoutTypes.map(({ name, ...rest }) => ({
          ...rest,
          name,
          value: name === 'all' ? balance : name === 'previous' ? previous_balance : 0,
        }));
      }
    },
  },
  computed: mapState({
    referrers: state => ({
      data: normalizeAdminReferrers(
        safeNullDataset(state)(['admin', 'referrersPending', 'data']),
        normalizeCb,
      ),
      meta: state.admin.referrersPending.meta,
    }),
    referralsPending: state => state.admin.referralsPending,
  }),
  methods: {
    async getReferrers(params) {
      return await this.$store.dispatch('admin/getReferrersPending', params);
    },
    addDollarSymbol(amount) {
      return addDollarSymbol(amount);
    },
    handleInputUpdate(name, value) {
      // update payout value only for custom type
      name === 'custom' && (this.payoutType = { ...this.payoutType, value });
      // update custom payout type value on the payoutTypes[] to persist value when moving accross inputs
      this.payoutTypes = this.payoutTypes.map(p =>
        name === 'custom' && p.name === 'custom' ? { ...p, value } : p,
      );
    },
    handleOrderDetails(items) {
      this.orderDetails = items;
      this.showModal = true;
    },
    handleCloseOrderDetails() {
      this.showModal = false;
      this.orderDetails = [];
    },
    handleClosePaymentModal() {
      this.showPaymentModal = false;
      this.payoutTypes = this.payoutTypes.map(p => (p.name === 'custom' ? { ...p, value: 0 } : p));
      this.payoutType = PAYOUT_TYPES[2];
      this.ratioValue = 'custom';
    },
    handlePaymentModal(data) {
      this.showPaymentModal = true;
      this.referrer = data;
    },
    handlePayReferral() {
      const { value: amount } = this.payoutType;
      const { id: customer_id } = this.referrer;

      this.$store.dispatch('admin/redeemReferral', { customer_id, amount });
      this.handleClosePaymentModal();
    },
    getReferralsByReferrer(id) {
      return async (params = {}) =>
        await this.$store.dispatch('admin/getReferralsPending', { id, params });
    },
  },
  styles: {},
  tNestedColumns: [],
  tColumns: [],
  async created() {
    /*
     * Parent Table Columns
     */
    const TABLE_COLUMNS = [
      { column: "referrer's name", isSort: false },
      { column: "referrer's tag type", isSort: false },
      { column: 'sales rep', isSort: false },
      { column: 'phone number', isSort: false },
      { column: 'email', isSort: false },
      { column: 'total balance', isSort: false },
    ];
    this.$options.tColumns = TABLE_COLUMNS;

    /*
     * Nested Table Columns
     */
    const TABLE_NESTED_COLUMNS = [
      { column: 'person referrerd', isSort: false },
      { column: 'order details', isSort: false },
      { column: 'date created', isSort: false },
      { column: 'shopify reward', isSort: false },
      { column: 'sales order', isSort: false },
      { column: 'invoice/memo #', isSort: false },
      { column: 'order total', isSort: false },
      { column: 'percentage earned', isSort: false },
      { column: 'referral value', isSort: false },
      { column: 'total balance', isSort: false },
    ];
    this.$options.tNestedColumns = TABLE_NESTED_COLUMNS;

    this.$options.styles = {
      display: 'grid',
      gridTemplateColumns:
        '50px repeat(2,minmax(200px, 1fr)) repeat(2, minmax(170px, 1fr)) minmax(230px, 1fr) repeat(2, minmax(120px, 1fr))',
    };
  },
};
</script>
